/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { observer } from 'mobx-react';
import * as React from 'react';
import { countries } from './wine-club-countries.data';
import WineClubInputDropdown from './wine-club-input-dropdown';
import WineClubInputText from './wine-club-input-text';
import { getInputs } from './wine-club-input.data';
import { IWineClubResources } from './wine-club.props.autogenerated';
import { IWineClubInputProps, IWineClubStepComponentProps } from './wine-club.types';

//==============================================================================
/**
 * WineClubShipping component
 * @extends {React.Component<IWineClubStepComponentProps>}
 */
//==============================================================================
@observer
class WineClubShipping extends React.Component<IWineClubStepComponentProps> {

    //==========================================================================
    // Variables
    //==========================================================================

    private resources: IWineClubResources;
    private isGifted?: boolean;

    //==========================================================================
    // Public Methods
    //==========================================================================

    public constructor(props: IWineClubStepComponentProps) {
        super(props);
        this.resources = this.props.resources || [];
        this.isGifted = this.props.fields && this.props.fields.membership.includes('gift');
        this._validateFields = this._validateFields.bind(this);
    }

    public render(): JSX.Element {
        const { resources } = this.props;

        return (
            <div className='wine-club-shipping'>
                <div className='wine-club-content'>
                    <h2 className='wine-club-subheading'>{resources && resources.wineClub_shippingHeading}</h2>
                    <p className='wine-club-text'>
                        {this.isGifted ? resources && resources.wineClub_shippingTextIsGifted : resources && resources.wineClub_shippingText}
                    </p>
                    <div className='wine-club-form'>

                        {/* Render address dropdown if membership is not a gift */}
                        {!this.isGifted && this._renderAddressDropdown()}

                        {/* Render address form if membership is a gift or if user wants to add an address not specified */}
                        {(this.isGifted || (this.props.isAddressSelected && !this.props.selectedAddress)) && this._renderShippingForm()}

                        {/* Render validation message if there's an error */}
                        {this.props.error &&
                            this.props.renderValidation(
                                resources && resources.wineClub_shippingErrorMessage,
                                'wine-club-error'
                            )
                        }

                        {/* Render back and next buttons */}
                        <div className='wine-club-button-group'>
                            {resources && resources.wineClub_buttonBack &&
                                this.props.renderButton(
                                    resources.wineClub_buttonBack,
                                    'membership',
                                    this.props.toggleStep,
                                    'wine-club-button-back'
                                )
                            }
                            {resources && resources.wineClub_buttonNext &&
                                this.props.renderButton(
                                    resources.wineClub_buttonNext,
                                    'confirmation',
                                    this.props.toggleStep,
                                    'wine-club-button-next',
                                    this._validateFields,
                                    this.props.handleNext
                                )
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    //==========================================================================
    // Private Methods
    //==========================================================================

    // Render dropdown to select current addresses.
    private _renderAddressDropdown(): JSX.Element {
        return (
            <div className='wine-club-input-group'>
                {this._renderAddresses()}
                {this.props.selectedAddress && this._renderSelectedAddress()}
            </div>
        );
    }

    // Renders select dropdown from user's current addresses.
    private _renderAddresses(): JSX.Element {
        const options: string[] = this.props.customerAddresses.map((address, index) => address.FullAddress || `Address ${index}`);
        const showForm: string = this.resources.wineClub_shippingSelectEnter;
        options.push(showForm);
        return (
            <WineClubInputDropdown
                id='shippingAddress'
                label={this.resources.wineClub_shippingSelectLabel}
                required={true}
                type='select'
                value={!this.props.isAddressSelected ? 'Select' : (this.props.selectedAddress ? this.props.selectedAddress.FullAddress : showForm)}
                options={options}
                handleChange={this.props.handleAddressChange}
            />
        );
    }

    // Renders user's selected address from dropdown.
    private _renderSelectedAddress(): JSX.Element {
        return (
            <div className='wine-club-selected-address'>
                <p className='wine-club-selected-address-line wine-club-selected-address-line-name'>{this.props.selectedAddress!.Name}</p>
                <p className='wine-club-selected-address-line wine-club-selected-address-line-address1'>{this.props.selectedAddress!.Street}</p>
                <p className='wine-club-selected-address-line wine-club-selected-address-line-citystatezip'>{this.props.selectedAddress!.City}, {this.props.selectedAddress!.State} {this.props.selectedAddress!.ZipCode}</p>
                <p className='wine-club-selected-address-line wine-club-selected-address-line-country'>{this.props.selectedAddress!.ThreeLetterISORegionName}</p>
                <p className='wine-club-selected-address-line wine-club-selected-address-line-phone'>{this.props.selectedAddress!.Phone}</p>
            </div>
        );
    }

    // Render shipping address form.
    private _renderShippingForm(): JSX.Element {
        return (
            <div className='wine-club-input-group'>
                {this._renderShippingInputs()}
                {this._renderShippingCountry()}
            </div>
        );
    }

    // Renders form text inputs from object data for the shipping step.
    private _renderShippingInputs(): JSX.Element[] {
        let shippingInputs = getInputs(this.resources).filter(input => input.group === 'shippingAddress' && input.type === 'text' && input.id !== 'email');
        const shippingInputsIsGifted = getInputs(this.resources).filter(input => input.group === 'shippingAddress' && input.type === 'text');
        this.isGifted && (shippingInputs = shippingInputsIsGifted);

        return shippingInputs.map((input, index) => {
            return (
                <WineClubInputText
                    key={index}
                    id={input.id}
                    label={input.label}
                    group={input.group}
                    required={input.required}
                    type={input.type}
                    value={this._getValue(input)}
                    validation={input.validation}
                    handleChange={this.props.handleChange}
                    renderValidation={this.props.renderValidation}
                />
            );
        });
    }

    // Sets value of input fields with form object variable for data binding.
    private _getValue(input: IWineClubInputProps): string {
        return input.group ? this.props.fields[input.group][input.id] : this.props.fields[input.id];
    }

    // Renders form dropdown input from object data for shipping country.
    private _renderShippingCountry(): JSX.Element {
        const country = getInputs(this.resources).find(input => input.id === 'country');
        return (
            <WineClubInputDropdown
                id='country'
                label={country!.label}
                group='shippingAddress'
                required={country!.required}
                type='select'
                value={this.props.fields.shippingAddress.country}
                options={countries}
                handleChange={this.props.handleChange}
            />
        );
    }

    // Validates all shipping fields.
    private _validateFields(): boolean {
        const shippingInputs = getInputs(this.resources).filter(input => input.group === 'shippingAddress');

        // Find text fields that need validation, then find inputs that fail the corresponding validation.
        const validationInputs = shippingInputs.filter(input =>  input.type === 'text' && input.validation);
        const invalidInput = validationInputs.find(input => !input.validation!.validate(this.props.fields.shippingAddress[input.id]));

        // Find required text fields that are empty.
        const requiredInputs = shippingInputs.filter(input => input.required === true);
        const emptyInput = requiredInputs.find(input => !this.props.fields.shippingAddress[input.id] || this.props.fields.shippingAddress[input.id] === 'Select');

        // If there is either an invalid or empty input, return false.
        return !(invalidInput || emptyInput);
    }
}

export default WineClubShipping;
